import { ScheduleInputField } from '@ps-design-system/components/ScheduleInputField';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  fetchDayAvailability,
  fetchMonthsAvailability
} from '../../services/schedule-service';

const createCalendarModifiers = (monthsAvailabilityObjs) => {
  const partiallyBooked = [];
  const fullyBooked = [];

  (monthsAvailabilityObjs || []).forEach((availabilityObj) => {
    Object.entries(availabilityObj.month_availability).forEach(
      ([day, data]) => {
        const modifiedDate = new Date(data.year, data.month - 1, day);
        if (data.is_fully_booked) {
          fullyBooked.push(modifiedDate);
          return;
        }
        if (data.is_partially_booked) {
          partiallyBooked.push(modifiedDate);
        }
      }
    );
  });

  return {
    partiallyBooked,
    fullyBooked
  };
};

const createPartiallyBlockedDays = (monthsAvailabilityObjs) => {
  const partiallyBlockedDays = [];

  (monthsAvailabilityObjs || []).forEach((availabilityObj) => {
    Object.entries(availabilityObj.month_availability).forEach(
      ([day, data]) => {
        if (!data.is_partially_booked || data.is_fully_booked) {
          return;
        }

        const partiallyAvailableDate = new Date(data.year, data.month - 1, day);
        partiallyBlockedDays.push({
          date: partiallyAvailableDate,
          blockedTimes: data?.booked_times,
          pctBlocked: data?.percent_booked
        });
      }
    );
  });

  return partiallyBlockedDays;
};

export const BookingScheduleInput = ({
  form,
  spaceId,
  locale,
  disabled,
  extendDayDisabled
}) => {
  const getCalendarModifiers = async (date, visibleMonths = 1) => {
    if (!date) {
      return [];
    }

    try {
      const monthsToFetch = Array.from({ length: visibleMonths }, (_, i) => {
        return new Date(date.getFullYear(), date.getMonth() + i);
      });

      const monthsAvailability = await fetchMonthsAvailability(
        spaceId,
        monthsToFetch
      );
      const modifiers = createCalendarModifiers(monthsAvailability);
      const partiallyBlockedDays =
        createPartiallyBlockedDays(monthsAvailability);
      return { modifiers, partiallyBlockedDays };
    } catch (error) {
      return [];
    }
  };

  const getTimeOptions = async (date, startTimeIndex) => {
    if (!date) {
      return [];
    }

    try {
      const timeOptions = await fetchDayAvailability({
        spaceId,
        date,
        startTimeIndex
      });

      return timeOptions;
    } catch (error) {
      return [];
    }
  };

  React.useEffect(() => {
    // Clear schedule errors when schedule changes
    form.clearErrors("schedule");
  }, [form.watch('schedule')]);

  return (
    <ScheduleInputField
      extendDayDisabled={extendDayDisabled}
      fullWidth={true}
      getCalendarModifiers={getCalendarModifiers}
      getTimeOptions={getTimeOptions}
      datePickerProps={{
        calendarProps: { locale },
        label: (
          <FormattedMessage
            id="booking_details_form.date_picker_label"
            defaultMessage="Date"
          />
        ),
        placeholder: (
          <FormattedMessage
            id="booking_details_form.date_picker_placeholder"
            defaultMessage="Select a date"
          />
        ),
        disabled
      }}
      timeSelectProps={{
        startTimeProps: {
          label: (
            <FormattedMessage
              id="booking_details_form.start_time_label"
              defaultMessage="Start time"
            />
          ),
          placeholder: (
            <FormattedMessage
              id="booking_details_form.start_time_placeholder"
              defaultMessage="Select a start time"
            />
          )
        },
        endTimeProps: {
          label: (
            <FormattedMessage
              id="booking_details_form.end_time_label"
              defaultMessage="End time"
            />
          ),
          placeholder: (
            <FormattedMessage
              id="booking_details_form.end_time_placeholder"
              defaultMessage="Select an end time"
            />
          )
        },
        disabled
      }}
    />
  );
};

BookingScheduleInput.displayName = 'BookingScheduleInput';
BookingScheduleInput.propTypes = {
  form: PropTypes.object,
  spaceId: PropTypes.string,
  locale: PropTypes.string,
  extendDayDisabled: PropTypes.bool
};
